import React, { useState, useEffect } from 'react';
import './ContactUs.css';
import { apiRequest, parseResponse, getAuthToken } from './utils/api';
import { useLanguage } from './i18n/LanguageContext';

const STATUS_LABEL = {
  OPEN: 'Open',
  PROCESS: 'In progress',
  CLOSED: 'Closed'
};

const ContactUs = ({ onBack, onOpenFaq }) => {
  const { t } = useLanguage();
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  // Get userId from localStorage
  const guestData = JSON.parse(localStorage.getItem('guestData') || '{}');
  const userId = guestData.userData?.data?.id || guestData.id || guestData.userId;

  useEffect(() => {
    let cancelled = false;
    const fetchHistory = async () => {
      if (!userId || !getAuthToken()) {
        setHistory([]);
        setLoadingHistory(false);
        return;
      }

      try {
        setLoadingHistory(true);
        const response = await apiRequest(`/faqs/history/user/${userId}`, {
          method: 'GET'
        });
        const data = await parseResponse(response);
        if (!cancelled && response.ok && data.status && Array.isArray(data.data)) {
          const sorted = [...data.data].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          setHistory(sorted);
        }
      } catch (err) {
        console.error('Error fetching FAQ history:', err);
      } finally {
        if (!cancelled) setLoadingHistory(false);
      }
    };

    fetchHistory();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const handleCallSupport = () => {
    // Support phone number - can be from API or config
    const supportPhone = '1800123456'; // Replace with actual support number
    window.location.href = `tel:${supportPhone}`;
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const chatDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      
      if (chatDate.getTime() === today.getTime()) {
        // Today - show "TODAY, HH:MM AM/PM"
        return `TODAY, ${date.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        })}`;
      } else {
        // Other dates - show date and time
        return date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
        }) + ', ' + date.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        });
      }
    } catch (err) {
      return '';
    }
  };

  const statusLabel = (status) => t(STATUS_LABEL[String(status || '').toUpperCase()] || 'Closed');

  return (
    <div className="contact-us-page">
      <div className="contact-header">
        {onBack && (
          <button className="contact-back-button" onClick={onBack} aria-label="Go back">
            <img
              src={process.env.PUBLIC_URL + '/images/back_button.svg'}
              alt=""
            />
          </button>
        )}
        <div className="contact-heading">
          <h1 className="contact-title">{t('Contact Us')}</h1>
          <p className="contact-subtitle">24/7 assistance for your journey.</p>
        </div>
      </div>

      {/* Support Card */}
      <div className="contact-cards">
        <div className="contact-card support-card">
          <div className="contact-card-icon support-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 14V10C3 6.68629 5.68629 4 9 4H15C18.3137 4 21 6.68629 21 10V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M21 14C21 15.1046 20.1046 16 19 16H18C16.8954 16 16 15.1046 16 14V12C16 10.8954 16.8954 10 18 10H19C20.1046 10 21 10.8954 21 12V14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3 14C3 15.1046 3.89543 16 5 16H6C7.10457 16 8 15.1046 8 14V12C8 10.8954 7.10457 10 6 10H5C3.89543 10 3 10.8954 3 12V14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="contact-card-content">
            <h2 className="contact-card-title">{t('moviq Support')}</h2>
            <p className="contact-card-subtitle">{t('Priority Support')}</p>
            <p className="contact-card-description">
              Instant assistance with your booking, app issues, or driver coordination.
            </p>
            <div className="contact-action-buttons-horizontal">
              {onOpenFaq && (
                <button type="button" className="contact-action-button chat-button-white" onClick={() => onOpenFaq(null)}>
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.8"/>
                    <path d="M9.5 9.2a2.5 2.5 0 1 1 3.3 2.4c-.7.3-1.3.8-1.3 1.6V14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                    <circle cx="12" cy="16.6" r="0.8" fill="currentColor"/>
                  </svg>
                  <span>{t('FAQs')}</span>
                </button>
              )}
              <button className="contact-action-button call-button" onClick={handleCallSupport}>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 16.92V19.92C22.0011 20.1985 21.9441 20.4742 21.8325 20.7292C21.7209 20.9841 21.5573 21.2126 21.3522 21.3999C21.1472 21.5872 20.9053 21.7289 20.6419 21.8157C20.3785 21.9025 20.0995 21.9324 19.822 21.902C16.7428 21.5857 13.787 20.5341 11.19 18.85C8.77382 17.3147 6.72533 15.2662 5.19 12.85C3.49997 10.2412 2.44824 7.27099 2.098 4.178C2.06758 3.90097 2.09726 3.62239 2.18489 3.35949C2.27252 3.09659 2.41598 2.85519 2.60498 2.65062C2.79399 2.44604 3.02419 2.28283 3.28058 2.17162C3.53697 2.06041 3.81399 2.00366 4.098 2.002H7.098C7.68123 1.99409 8.24251 2.16755 8.70737 2.49455C9.17224 2.82155 9.51507 3.28624 9.688 3.828L10.878 7.228C10.9543 7.45696 10.9814 7.69971 10.9573 7.93952C10.9332 8.17933 10.8584 8.41019 10.738 8.616L8.738 11.616C9.80219 13.4883 11.5117 15.1978 13.384 16.262L16.384 14.262C16.5898 14.1416 16.8207 14.0668 17.0605 14.0427C17.3003 14.0186 17.543 14.0457 17.772 14.122L21.172 15.312C21.7138 15.4849 22.1785 15.8278 22.5055 16.2926C22.8325 16.7575 23.0059 17.3188 22.998 17.902L22.998 17.902Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{t('CALL SUPPORT')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="past-interactions-section">
        <h2 className="past-interactions-title">{t('Previous FAQs')}</h2>
        {loadingHistory ? (
          <div className="past-interactions-loading">
            <p>{t('Loading...')}</p>
          </div>
        ) : !userId || !getAuthToken() ? (
          <div className="past-interactions-empty">
            <p>{t('Sign in to see your FAQ history.')}</p>
          </div>
        ) : history.length === 0 ? (
          <div className="past-interactions-empty">
            <p>{t('No previous FAQs')}</p>
          </div>
        ) : (
          <div className="past-interactions-list">
            {history.map((item) => {
              const status = String(item.chatStatus || 'CLOSED').toLowerCase();
              return (
                <button
                  type="button"
                  key={item.id || `${item.faqId}-${item.createdAt}`}
                  className="past-interaction-card"
                  onClick={() => onOpenFaq && onOpenFaq(item)}
                >
                  <div className="past-interaction-header">
                    <div className="past-interaction-leading">
                      <span className="past-interaction-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="4" y="3" width="16" height="18" rx="2" stroke="currentColor" strokeWidth="1.8"/>
                          <path d="M8 8H16M8 12H16M8 16H13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                        </svg>
                      </span>
                      <span className="past-interaction-date">
                        {formatDateTime(item.createdAt)}
                      </span>
                    </div>
                    <span className={`past-interaction-status ${status}`}>
                      {statusLabel(item.chatStatus)}
                    </span>
                  </div>
                  <p className="faq-history-question">{item.question}</p>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactUs;
